import { useMemo, useRef, useState, type MouseEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { Loader2, UploadCloud, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { api, type MetaAdsAdAccount } from "@/lib/api";

/**
 * What Meta takes into an ad account's media library.
 *
 * A GIF is an image everywhere except here — Meta serves it as a video, so it
 * goes to /advideos and comes back with a video id rather than an image hash.
 * The size ceilings are Meta's own.
 */
const ACCEPTED: Record<string, { kind: "image" | "video"; label: string }> = {
	"image/jpeg": { kind: "image", label: "JPG" },
	"image/png": { kind: "image", label: "PNG" },
	"image/gif": { kind: "video", label: "GIF" },
	"video/mp4": { kind: "video", label: "MP4" },
	"video/quicktime": { kind: "video", label: "MOV" },
};

const MAX_BYTES = { image: 30 * 1024 * 1024, video: 4 * 1024 * 1024 * 1024 };

const ACCEPT_ATTR = Object.keys(ACCEPTED).join(",");
const ACCEPTED_LABELS = [...new Set(Object.values(ACCEPTED).map((a) => a.label))].join(", ");

type Item = {
	file: File;
	status: "pending" | "uploading" | "done" | "error";
	error?: string;
};

function formatSize(bytes: number): string {
	if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
	if (bytes >= 1024 * 1024) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
	return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Rejects what Meta would reject, before the request is made.
 *
 * Only type and size — resolution, aspect ratio, duration and codec need the
 * file decoded, and Meta says more about those than a guess here would.
 */
function reject(file: File): string | null {
	const accepted = ACCEPTED[file.type];
	if (!accepted) return `Not supported. Meta takes ${ACCEPTED_LABELS}.`;

	const limit = MAX_BYTES[accepted.kind];
	if (file.size > limit) {
		return accepted.kind === "image"
			? "Larger than 30 MB, Meta's limit for images."
			: "Larger than 4 GB, Meta's limit for videos.";
	}

	return null;
}

export function UploadCreativesModal({
	open,
	onClose,
	teamId,
}: {
	open: boolean;
	onClose: () => void;
	teamId?: string | null;
}) {
	const queryClient = useQueryClient();
	const inputRef = useRef<HTMLInputElement>(null);

	const [items, setItems] = useState<Item[]>([]);
	const [account, setAccount] = useState<string | null>(null);

	const { data, isLoading } = useQuery({
		queryKey: ["meta-ads-ad-accounts", teamId ?? null],
		queryFn: () => api.getMetaAdsAdAccounts(teamId),
		enabled: open,
	});

	// An account without a token cannot be uploaded to at all, so it is not worth
	// offering as a choice.
	const usable = useMemo(
		() => (data?.adAccounts ?? []).filter((a: MetaAdsAdAccount) => a.hasToken),
		[data],
	);

	const selected = account ?? usable.find((a) => a.isDefault)?.dbId ?? usable[0]?.dbId ?? null;

	const uploadMutation = useMutation({
		mutationFn: async () => {
			const queue = items.filter((item) => item.status === "pending" && !reject(item.file));

			// One at a time so a rejection lands on the file that caused it, rather
			// than failing the whole batch with one message.
			for (const item of queue) {
				setItems((prev) =>
					prev.map((i) => (i.file === item.file ? { ...i, status: "uploading" } : i)),
				);

				try {
					await api.uploadMetaAdsMedia({
						file: item.file,
						adAccount: selected ?? undefined,
						teamId,
					});
					setItems((prev) =>
						prev.map((i) => (i.file === item.file ? { ...i, status: "done" } : i)),
					);
				} catch (err) {
					setItems((prev) =>
						prev.map((i) =>
							i.file === item.file
								? {
										...i,
										status: "error",
										error: err instanceof Error ? err.message : "Upload failed",
									}
								: i,
						),
					);
				}
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["meta-ads-media"] });
		},
	});

	function addFiles(files: File[]) {
		setItems((prev) => [
			...prev,
			...files.map((file) => ({
				file,
				status: "pending" as const,
				error: reject(file) ?? undefined,
			})),
		]);
	}

	function close() {
		if (uploadMutation.isPending) return;
		setItems([]);
		onClose();
	}

	const ready = items.filter((i) => i.status === "pending" && !i.error).length;

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-scrim/30 backdrop-blur-sm"
					onClick={close}
				>
					<motion.div
						initial={{ scale: 0.95, opacity: 0, y: 20 }}
						animate={{ scale: 1, opacity: 1, y: 0 }}
						exit={{ scale: 0.95, opacity: 0, y: 20 }}
						transition={{ type: "spring", damping: 25, stiffness: 300 }}
						className="relative w-full max-w-lg bg-bg-surface border border-border-subtle rounded-2xl shadow-popover flex flex-col max-h-[85vh]"
						onClick={(e: MouseEvent) => e.stopPropagation()}
					>
						<div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
							<h3 className="text-text-primary font-semibold">Upload creatives</h3>
							<Button
								variant="ghost"
								size="icon-sm"
								onClick={close}
								disabled={uploadMutation.isPending}
							>
								<X size={18} />
							</Button>
						</div>

						<div className="px-6 py-5 overflow-y-auto space-y-4">
							{isLoading && (
								<div className="flex items-center gap-2 text-text-tertiary text-sm">
									<Loader2 size={14} className="animate-spin" />
									Loading ad accounts…
								</div>
							)}

							{!isLoading && !usable.length && (
								<p className="text-sm text-text-secondary">
									No Meta ad account is connected. Connect one in settings first.
								</p>
							)}

							{/* One account is not a choice — the selector only appears when there is
							    something to decide. */}
							{usable.length > 1 && (
								<label className="block">
									<span className="text-xs text-text-tertiary">Ad account</span>
									<Select
										value={selected ?? ""}
										onChange={(e) => setAccount(e.target.value)}
										disabled={uploadMutation.isPending}
										className="mt-1"
									>
										{usable.map((a) => (
											<option key={a.dbId} value={a.dbId} className="bg-bg-surface">
												{a.name} · act_{a.adAccountId}
											</option>
										))}
									</Select>
								</label>
							)}

							{Boolean(usable.length) && (
								<>
									<button
										onClick={() => inputRef.current?.click()}
										disabled={uploadMutation.isPending}
										className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-xl border border-dashed border-border-control text-text-secondary hover:bg-bg-sunken hover:text-text-primary transition-colors disabled:bg-action-disabled-bg disabled:text-action-disabled-fg disabled:cursor-not-allowed"
									>
										<UploadCloud size={22} />
										<span className="text-sm">Choose files</span>
										<span className="text-xs text-text-tertiary">{ACCEPTED_LABELS}</span>
									</button>

									<input
										ref={inputRef}
										type="file"
										multiple
										accept={ACCEPT_ATTR}
										className="hidden"
										onChange={(e) => {
											addFiles(Array.from(e.target.files ?? []));
											e.target.value = "";
										}}
									/>

									{items.length > 0 && (
										<ul className="space-y-1.5">
											{items.map((item, index) => (
												<li
													key={`${item.file.name}-${index}`}
													className="flex items-start gap-3 text-sm px-3 py-2 rounded-lg bg-bg-canvas"
												>
													<div className="flex-1 min-w-0">
														<p className="text-text-primary truncate">{item.file.name}</p>
														<p
															className={`text-xs ${item.error ? "text-danger" : "text-text-tertiary"}`}
														>
															{item.error ?? formatSize(item.file.size)}
														</p>
													</div>
													<span className="text-xs text-text-tertiary shrink-0 pt-0.5">
														{item.status === "uploading" && (
															<Loader2 size={13} className="animate-spin" />
														)}
														{item.status === "done" && "✓"}
													</span>
												</li>
											))}
										</ul>
									)}
								</>
							)}
						</div>

						{Boolean(usable.length) && (
							<div className="px-6 py-4 border-t border-border-subtle flex justify-end gap-2">
								<Button
									variant="ghost"
									onClick={close}
									disabled={uploadMutation.isPending}
								>
									Close
								</Button>
								<Button
									onClick={() => uploadMutation.mutate()}
									disabled={uploadMutation.isPending || !ready || !selected}
								>
									{uploadMutation.isPending ? (
										<>
											<Loader2 size={14} className="animate-spin" />
											Uploading…
										</>
									) : (
										`Upload${ready ? ` (${ready})` : ""}`
									)}
								</Button>
							</div>
						)}
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
